import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, output, signal, WritableSignal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DateUtil } from '../../../util/DateUtil';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { SnackbarService } from '../../../services/snackbar.service';
import { SessionService } from '../../../services/session.service';
import {
	ConsumptionInfoReceiverService,
	OwnerService,
	Person,
	RealEstate,
	RealEstateService,
	Resident,
	ResidentService,
	TenantService,
	TenantUnitDetail,
	TenantUnitService,
	VacancyService,
} from '@eadsysteme/ewsrealestateclient-v3';
import { EnergyEfficiencySettingService, RealEstateSetting, RoleSetting } from '@eadsysteme/energyefficiencyclient-v1';
import { fadeInUp400ms } from '@shared/animations/fade-in-up.animation';
import { MatDialog } from '@angular/material/dialog';
import { Role, RoleModel, RoleType, UiRoleType } from 'src/app/services/RoleModel';
import { catchError, Observable, of, Subject, switchMap } from 'rxjs';
import { Permission } from '../../../services/data/permission';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { ARA_GROUP_ROW_DEFS, flattenGroups, GroupedRow, TableGroup, toggleSetKey } from '@shared/ui/table-grouping';
import { AraTableGroupColumnsComponent } from '@shared/ui/ara-table-group-columns/ara-table-group-columns.component';

import { AraButtonComponent } from '@shared/ui/ara-button/ara-button.component';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatBadge } from '@angular/material/badge';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { AraButtonToggleGroupComponent } from '@shared/ui/ara-button-toggle-group/ara-button-toggle-group.component';
import { AraButtonToggleComponent } from '@shared/ui/ara-button-toggle-group/ara-button-toggle.component';
import { AraGenericChipComponent } from '@shared/ui/ara-generic-chip/ara-generic-chip.component';
import { RolesHistoryComponent } from './roles-history/roles-history.component';
import { RolesBranchTimelineComponent } from './roles-branch-timeline/roles-branch-timeline.component';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS, MAT_DAYJS_DATE_FORMATS } from '../../../util/material-dayjs-adapter';
import { TimePeriod } from './time-period.service';
import { RoleModelService } from './role-model.service';
import { PermissionState, RolePermissionsService } from './role-permissions.service';
import { PropertyManagementRightsService } from './property-management-rights.service';
import { NO_UVI_RECEPTION_RIGHTS, UviReceptionRights, UviReceptionRightsService } from './uvi-reception-rights.service';
import { DeleteRoleWarningDialogComponent } from './delete-role-warning-dialog.component';
import { PersonEditorDialogComponent } from './person-editor-dialog/person-editor-dialog.component';
import { PersonEditorDialogData, PersonEditorEntryContext } from './person-editor-dialog/person-editor-dialog.types';
import { EditorTabContext, PersonEditorDataFactory } from './person-editor-dialog/person-editor-data.factory';
import { RolesDisplayService } from './roles-display.service';
import { isProtectedRoleType, roleHasEnd, roleTrackKey } from './roles-view-model';
import { roleStatusIcon as statusIconForStatus } from '../../util/role-history-popover/role-history-popover.component';
import { PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { AraPopoverComponent } from '@shared/ui/popover/popover.component';
import { PersonDisplayUtil } from '../../../util/PersonDisplayUtil';
import { AraPermissionWidgetComponent, PermissionDialogParameters } from '@shared/ui/ara-permission-widget/ara-permission-widget.component';
import { TenantUnitReloadService } from '../../../services/tenant-unit-reload.service';
import { AraGenericTableComponent } from '@shared/ui/ara-generic-table/ara-generic-table.component';
import { TableColumn } from '@shared/interfaces/table-column.interface';
import { TableScrollBodyDirective } from '../../../directives/table-scroll-body.directive';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatRow,
	MatRowDef,
	MatTable,
} from '@angular/material/table';
import { AraLinkComponent } from '@shared/ui/ara-link/ara-link.component';
import SyncLevelEnum = RealEstate.SyncLevelEnum;
import RoleTypeEnum = RoleSetting.RoleTypeEnum;

export type RoleViewMode = 'list' | 'history' | 'timeline';

/** Berechtigungscodes sind fachlich nur für Eigentümer und Bewohner vorgesehen. */
const PERMISSION_CODE_ROLE_TYPES: readonly UiRoleType[] = [UiRoleType.OWNER, UiRoleType.RESIDENT];

/** Ein Eintrag (Person + konkrete Rolle) innerhalb einer festen Rollen-Sektion der Liste. */
export interface RoleSectionEntry {
	roleModel: RoleModel;
	role: Role;
}

@Component({
	selector: 'ara-roles-view',
	templateUrl: './roles-view.component.html',
	styleUrls: ['./roles-view.component.scss'],
	imports: [
		TranslocoDirective,
		AraButtonComponent,
		MatIcon,
		MatProgressSpinner,
		ReactiveFormsModule,
		FormsModule,
		MatBadge,
		MatSlideToggle,
		AraButtonToggleGroupComponent,
		AraButtonToggleComponent,
		AraGenericChipComponent,
		RolesHistoryComponent,
		RolesBranchTimelineComponent,
		AraPopoverComponent,
		AraPermissionWidgetComponent,
		AraGenericTableComponent,
		AraTableGroupColumnsComponent,
		TableScrollBodyDirective,
		AraLinkComponent,
		MatTable,
		MatColumnDef,
		MatHeaderCell,
		MatHeaderCellDef,
		MatCell,
		MatCellDef,
		MatHeaderRow,
		MatHeaderRowDef,
		MatRow,
		MatRowDef,
		MatSort,
		MatSortHeader,
		NgTemplateOutlet,
	],
	animations: [fadeInUp400ms],
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [
		{ provide: DateAdapter, useClass: DayjsDateAdapter, deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS] },
		{ provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_DATE_FORMATS },
	],
})
export class RolesViewComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly activeRoute = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly translocoService = inject(TranslocoService);
	private readonly dialog = inject(MatDialog);
	private readonly snackBar = inject(SnackbarService);
	private readonly residentService = inject(ResidentService);
	private readonly tenantService = inject(TenantService);
	private readonly ownerService = inject(OwnerService);
	private readonly consumptionInfoReceiverService = inject(ConsumptionInfoReceiverService);
	private readonly vacancyService = inject(VacancyService);
	private readonly realEstateService = inject(RealEstateService);
	private readonly energyEfficiencySettingService = inject(EnergyEfficiencySettingService);
	private readonly tenantUnitService = inject(TenantUnitService);
	private readonly roleModelService = inject(RoleModelService);
	private readonly rolePermissionsService = inject(RolePermissionsService);
	private readonly uviReceptionRightsService = inject(UviReceptionRightsService);
	private readonly propertyManagementRightsService = inject(PropertyManagementRightsService);
	private readonly personDisplayUtil = inject(PersonDisplayUtil);
	private readonly tenantUnitReloadService = inject(TenantUnitReloadService);
	private readonly editorDataFactory = inject(PersonEditorDataFactory);
	private readonly rolesDisplay = inject(RolesDisplayService);

	protected readonly UiRoleType = UiRoleType;

	public readonly dateUtilService = inject(DateUtil);
	public readonly sessionService = inject(SessionService);

	private readonly MAX_STRING_LENGTH = 20;

	roleModels: RoleModel[] = [];
	/** Aktive Personen-Ansicht (Liste / Verlauf / Zeitstrahl). */
	readonly viewMode = signal<RoleViewMode>('list');

	/** Geladene RoleModels als Signal — Grundlage der sortierten Ansichts-Liste. */
	private readonly roleModelsData = signal<RoleModel[]>([]);
	/** Alle Personen/Leerstände in Anzeige-Reihenfolge (reagiert auf Daten + Sortier-Modus). */
	protected readonly sortedRoleModels = computed(() => this.roleModelsData());

	/** Feste Reihenfolge der Rollen-Sektionen (rollenzentrierte Liste). Leerstand fällt in die Nutzer-Sektion. */
	private readonly ROLE_SECTION_ORDER: RoleType[] = [UiRoleType.OWNER, UiRoleType.RESIDENT, UiRoleType.TENANT, UiRoleType.CONSUMPTION_INFO_RECEIVER];

	/** Freitextsuche der Rollen-Liste (Wert kommt aus der ara-generic-table-Shell). */
	protected readonly roleSearch = signal('');
	readonly roleSearch$ = new Subject<string>();
	/** Aktive Spaltensortierung (wird innerhalb der Gruppen angewandt). */
	private readonly roleSort = signal<Sort>({ active: '', direction: '' });
	/** Eingeklappte Rollen-Gruppen (Schlüssel = Rollentyp als String). */
	protected readonly collapsedRoleGroups = signal<ReadonlySet<string>>(new Set());
	/** Gruppen, in denen „Alle anzeigen" aktiv ist (zeigt auch die vergangenen Rollen). */
	protected readonly expandedRoleGroups = signal<ReadonlySet<string>>(new Set());
	/** Spalten-IDs + `when`-Prädikate der drei Marker-Row-Defs (Zellen liefert ara-table-group-columns). */
	protected readonly groupRowDefs = ARA_GROUP_ROW_DEFS;

	/**
	 * Rollen-Gruppen der einen gruppierten Tabelle: je Rollentyp alle Einträge (Verlauf integriert),
	 * gefiltert nach Suche und innerhalb der Gruppe sortiert. Leerstand fällt in die Nutzer-Gruppe.
	 */
	protected readonly roleTableGroups = computed<TableGroup<RoleSectionEntry>[]>(() => {
		const models = this.sortedRoleModels();
		const showTenant = this.personRoleTypes().includes(UiRoleType.TENANT);
		const search = this.roleSearch().trim().toLowerCase();
		const sort = this.roleSort();
		return this.ROLE_SECTION_ORDER.filter((type) => type !== UiRoleType.TENANT || showTenant).map((type) => {
			let rows: RoleSectionEntry[] = [];
			for (const roleModel of models) {
				for (const role of roleModel.roles ?? []) {
					const groupType = role.roleType === UiRoleType.VACANCY ? UiRoleType.RESIDENT : role.roleType;
					if (groupType === type) {
						rows.push({ roleModel, role });
					}
				}
			}
			if (search) {
				rows = rows.filter((entry) => this.entryMatchesSearch(entry, search));
			}
			rows = this.sortSectionEntries(rows, sort);
			return { key: String(type), label: this.getRoleTypeLabel(type), rows, meta: type };
		});
	});

	/**
	 * Flache Datasource (Kopfzeile → Datenzeilen → „Alle anzeigen" → Add-Zeile je Gruppe). Sichtbar
	 * sind nur heutige und zukünftige Rollen, damit die Liste über die Jahre nicht endlos wird.
	 */
	protected readonly roleFlatRows = computed<GroupedRow<RoleSectionEntry>[]>(() =>
		flattenGroups(this.roleTableGroups(), {
			collapsedKeys: this.collapsedRoleGroups(),
			addRow: true,
			hiddenRows: (entry) => DateUtil.classifyPeriod(entry.role.validFrom, entry.role.validTo) === 'past',
			expandedKeys: this.expandedRoleGroups(),
		})
	);

	/** Spalten der Sektions-Tabelle (reduziert): Person · Zeitraum · Berechtigung · UVI · HV-Nummer (+ Aktionen). */
	protected readonly roleSectionColumns: TableColumn<RoleSectionEntry>[] = [
		{ label: 'tenantunitDetails.person', property: 'person', type: 'text', visible: true },
		{ label: 'tenantunitDetails.editor.period', property: 'period', type: 'text', visible: true },
		{ label: 'permissions.permissions', property: 'permissions', type: 'text', visible: true },
		{ label: 'tenantunitDetails.uviOptions', property: 'uvi', type: 'text', visible: true },
		{ label: 'tenantunitDetails.tenantNumber', property: 'hv', type: 'text', visible: true },
	];

	currentRealEstateArea: number;
	currentRealEstateId: string;
	currentTenantUnitId: string;

	readonly rolesChanged = output<void>();

	readonly selectedRealEstate: WritableSignal<RealEstate> = signal(null);
	readonly selectedTenantUnit: WritableSignal<TenantUnitDetail> = signal(null);
	readonly finishedLoading = signal(false);
	readonly realEstateSettings = signal<RealEstateSetting | null>(null);
	/** Darf der eingeloggte Benutzer die uVI-Zustellart ändern? Kommt aus der Liegenschafts-Einstellung. */
	private readonly uviReceptionRights = signal<UviReceptionRights>(NO_UVI_RECEPTION_RIGHTS);
	protected readonly isPropertyManagement = signal(false);

	readonly personRoleTypes = computed(() => this.computePersonRoleTypes());

	availablePeriodsByRoleType: Map<RoleTypeEnum, TimePeriod[]> = new Map();

	private readonly permissionState: PermissionState = {
		permissions: {},
		permissionStrings: {},
		isRegistered: {},
	};
	private personIdToHighlightNextLoad: string | null = null;

	get permissions(): { [key: string]: Permission[] } {
		return this.permissionState.permissions;
	}

	get isRegistered(): { [key: string]: boolean } {
		return this.permissionState.isRegistered;
	}

	constructor() {
		// Suchwert aus der ara-generic-table-Shell in ein Signal spiegeln (Filterung erfolgt in roleTableGroups).
		this.roleSearch$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => this.roleSearch.set(value ?? ''));
	}

	ngOnInit(): void {
		this.initializeContextData();
		this.loadPropertyManagementRight();
		this.activeRoute.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (params) => {
				const realEstateId = params.get('realEstateId') ?? this.currentRealEstateId;
				const tenantUnitId = params.get('tenantUnitId') ?? this.currentTenantUnitId;
				if (!realEstateId || !tenantUnitId) {
					return;
				}
				const realEstateChanged = this.currentRealEstateId !== realEstateId;
				const tenantUnitChanged = this.currentTenantUnitId !== tenantUnitId;
				this.currentRealEstateId = realEstateId;
				this.currentTenantUnitId = tenantUnitId;
				if (realEstateChanged || !this.selectedRealEstate()) {
					this.loadRealEstateData();
				} else if (tenantUnitChanged || !this.selectedTenantUnit()) {
					this.loadTenantUnitData();
				}
			},
		});
	}

	private initializeContextData(): void {
		this.currentTenantUnitId = this.activeRoute.snapshot.paramMap.get('tenantUnitId');
		this.currentRealEstateArea = Number(sessionStorage.getItem('selectedRealEstateArea') ?? this.sessionService.effectiveArea);
		this.currentRealEstateId = sessionStorage.getItem('selectedRealEstateID') ?? this.activeRoute.snapshot.paramMap.get('realEstateId');
	}

	private loadRealEstateData(): void {
		this.realEstateService
			.getRealEstate(this.currentRealEstateArea, this.currentRealEstateId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (realEstate) => this.handleRealEstateLoaded(realEstate),
				error: (err) => this.handleLoadError(err),
			});
	}

	private handleRealEstateLoaded(realEstate: RealEstate): void {
		this.selectedRealEstate.set(realEstate);
		this.energyEfficiencySettingService
			.getRealEstateSetting(this.currentRealEstateArea, realEstate.id)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (settings) => this.realEstateSettings.set(settings),
			});
		this.loadUviReceptionRights(realEstate.id);
		this.loadTenantUnitData();
	}

	private loadPropertyManagementRight(): void {
		this.propertyManagementRightsService
			.resolve()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((isPropertyManagement) => this.isPropertyManagement.set(isPropertyManagement));
	}

	private loadUviReceptionRights(realEstateId: string | null): void {
		this.uviReceptionRightsService
			.resolve(this.currentRealEstateArea, realEstateId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((rights) => this.uviReceptionRights.set(rights));
	}

	private handleLoadError(err: unknown): void {
		this.snackBar.apiError(err, this.translocoService.translate('tenantunitDetails.errorLoadingData'));
	}

	loadTenantUnitData(): void {
		this.finishedLoading.set(false);

		this.tenantUnitService
			.getTenantUnitById(this.currentRealEstateArea, this.currentRealEstateId, this.currentTenantUnitId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (tenantUnit) => this.handleTenantUnitLoaded(tenantUnit),
				error: (err) => this.handleTenantUnitLoadError(err),
			});
	}

	private pendingRoleChangeNotification = false;

	protected notifyRoleChange(): void {
		this.pendingRoleChangeNotification = true;
		this.loadTenantUnitData();
	}

	private handleTenantUnitLoaded(tenantUnit: TenantUnitDetail): void {
		this.selectedTenantUnit.set(tenantUnit);
		this.buildAndSetRoleModels(tenantUnit);
		this.processHighlighting();
		this.finishedLoading.set(true);
		if (this.pendingRoleChangeNotification) {
			this.pendingRoleChangeNotification = false;
			this.rolesChanged.emit();
			// Notify the tenant-shares Gantt to reload with the fresh role data
			this.tenantUnitReloadService.tenantUnitSaved$.next();
		}
	}

	private handleTenantUnitLoadError(err: unknown): void {
		this.snackBar.apiError(err, this.translocoService.translate('tenantunitDetails.errorLoadingData'));
		this.finishedLoading.set(true);
	}

	private buildAndSetRoleModels(tenantUnit: TenantUnitDetail): void {
		const hideTenants = !this.personRoleTypes().includes(UiRoleType.TENANT);
		const result = this.roleModelService.buildRoleModels(tenantUnit, hideTenants);

		this.availablePeriodsByRoleType = result.availablePeriodsByRoleType;

		this.processAllRoles(result.roleModels);
		this.roleModels = this.sortRoleModelsByTodayPresence(result.roleModels);
		this.roleModelsData.set([...this.roleModels]);
	}

	private processAllRoles(roleModels: RoleModel[]): void {
		const rolesByType = this.groupRolesByType(roleModels);
		const vacancyPeriods = (rolesByType.get(UiRoleType.VACANCY) ?? [])
			.filter((role) => role.validFrom && role.validTo)
			.map((role) => ({ start: role.validFrom, end: role.validTo }));

		for (const [roleType, rolesOfType] of rolesByType) {
			const relevantVacancyPeriods = roleType === UiRoleType.VACANCY ? [] : vacancyPeriods;
			this.roleModelService.sortAndMarkOverlappingRoles(rolesOfType, relevantVacancyPeriods);
		}
	}

	private groupRolesByType(roleModels: RoleModel[]): Map<string, Role[]> {
		const rolesByType = new Map<string, Role[]>();

		for (const roleModel of roleModels) {
			for (const role of roleModel.roles) {
				const type = role.roleType;

				if (!rolesByType.has(type)) {
					rolesByType.set(type, []);
				}

				rolesByType.get(type).push(role);
				this.loadPermissionsForRole(role);
				this.loadRoleSettings(role);
			}
		}

		return rolesByType;
	}

	/** Basis-Reihenfolge: heute gültige Personen zuerst (führend), Reihenfolge sonst stabil. */
	private sortRoleModelsByTodayPresence(roleModels: RoleModel[]): RoleModel[] {
		return roleModels
			.map((roleModel, index) => ({ roleModel, index }))
			.sort((a, b) => {
				const aToday = this.roleModelService.hasTodayRole(a.roleModel);
				const bToday = this.roleModelService.hasTodayRole(b.roleModel);
				if (aToday !== bToday) {
					return aToday ? -1 : 1;
				}
				return a.index - b.index;
			})
			.map(({ roleModel }) => roleModel);
	}

	private loadPermissionsForRole(role: Role): void {
		this.rolePermissionsService.loadPermissionsForRole(role, this.permissionState, this.destroyRef, this.MAX_STRING_LENGTH);
	}

	private loadRoleSettings(role: Role): void {
		this.energyEfficiencySettingService
			.getRoleSetting(this.selectedRealEstate().area, role.id)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (roleSetting) => (role.roleSetting = roleSetting),
				error: () => (role.roleSetting = this.createDefaultRoleSetting(role.id)),
			});
	}

	private createDefaultRoleSetting(roleId: string): RoleSetting {
		return {
			receptionType: 'PRINT',
			roleId: roleId,
			roleType: RoleSetting.RoleTypeEnum.RESIDENT,
		};
	}

	private computePersonRoleTypes(): RoleType[] {
		const realEstate = this.selectedRealEstate();
		const allRoleTypes = Object.values(UiRoleType).filter((value) => value !== UiRoleType.VACANCY);

		if (this.shouldHideTenantRole(realEstate)) {
			return allRoleTypes.filter((roleType) => roleType !== UiRoleType.TENANT);
		}

		return allRoleTypes;
	}

	private shouldHideTenantRole(realEstate: RealEstate): boolean {
		if (realEstate?.syncLevel == null) {
			return false;
		}

		return realEstate.syncLevel === SyncLevelEnum.LEVEL_0 || realEstate.syncLevel === SyncLevelEnum.LEVEL_1;
	}

	hasActiveVacancy(): boolean {
		return this.roleModelsData().some((roleModel) =>
			(roleModel.roles ?? []).some((role) => role.roleType === UiRoleType.VACANCY && this.roleModelService.isCurrentRole(role))
		);
	}

	// --- Personen-/Rollen-Editor (ara-person-editor-dialog) -------------------------------------

	openPersonEditorResident(): void {
		this.openPersonEditorDialog('resident');
	}

	openPersonEditorOwner(): void {
		if (!this.isPropertyManagement()) {
			return;
		}
		this.openPersonEditorDialog('owner');
	}

	openPersonEditorVacancy(): void {
		if (this.hasActiveVacancy()) {
			return;
		}
		this.openPersonEditorDialog('vacancy');
	}

	/** Die uVI hängt am Bewohner: andere Rollen haben keine, ohne aktuellen Bewohner fehlt der Datensatz. */
	canJumpToUvi(role: Role): boolean {
		return role.roleType === UiRoleType.RESIDENT && !!this.selectedTenantUnit()?.currentResident;
	}

	uviJumpTooltipKey(role: Role): string {
		if (role.roleType !== UiRoleType.RESIDENT) {
			return 'uvi.jumpToUviNotResident';
		}
		if (!this.selectedTenantUnit()?.currentResident) {
			return 'uvi.jumpToUviNoResident';
		}
		return 'uvi.jumpToUvi';
	}

	routeToUvi(): void {
		this.router.navigate(['/realestates/' + this.selectedRealEstate().id + '/tenantunits/' + this.selectedTenantUnit().id + '/uvi']);
	}

	/** Öffnet den Editor zum Bearbeiten einer bestehenden Person/Rolle (Pen-Action). */
	openPersonEditorEdit(roleModel: RoleModel, event?: Event): void {
		event?.stopPropagation();
		if (!this.canEditRoleModel(roleModel)) {
			this.snackBar.warn(this.translocoService.translate('tenantunitDetails.editor.protectedRoleReadonly'));
			return;
		}
		this.openEditor({ entryContext: 'edit', ...this.editorDataFactory.buildEditData(this.editorTabContext(), roleModel) });
	}

	/** Person und Zustelladresse gelten für alle Rollen der Person — eine Eigentümer-Rolle sperrt sie ganz. */
	protected canEditRoleModel(roleModel: RoleModel): boolean {
		return this.isPropertyManagement() || !this.hasProtectedRole(roleModel);
	}

	private hasProtectedRole(roleModel: RoleModel): boolean {
		return (roleModel?.roles ?? []).some((role) => isProtectedRoleType(role.roleType));
	}

	protected canAddRoleType(roleType: RoleType): boolean {
		return !isProtectedRoleType(roleType) || this.isPropertyManagement();
	}

	protected personEditTooltipKey(roleModel: RoleModel): string {
		return this.canEditRoleModel(roleModel) ? 'tenantunitDetails.personEdit' : 'tenantunitDetails.editor.protectedRoleReadonly';
	}

	protected deleteRoleTooltipKey(role: Role): string {
		if (!this.canDeleteProtectedRole(role)) {
			return 'tenantunitDetails.editor.protectedRoleReadonly';
		}
		return this.canDeleteRole(role) ? 'tenantunitDetails.roleDelete' : 'tenantunitDetails.deleteLatestRoleFirst';
	}

	private openPersonEditorDialog(entryContext: PersonEditorEntryContext, preselectedRoleTypes: RoleType[] = [], excludedPerson?: Person): void {
		this.openEditor({ entryContext, ...this.editorDataFactory.buildAddData(this.editorTabContext(), preselectedRoleTypes, excludedPerson) });
	}

	/** Aktueller Tab-Zustand als Eingabe für die {@link PersonEditorDataFactory}. */
	private editorTabContext(): EditorTabContext {
		return {
			realEstate: this.selectedRealEstate(),
			tenantUnit: this.selectedTenantUnit(),
			realEstateArea: this.currentRealEstateArea,
			realEstateId: this.currentRealEstateId,
			tenantUnitId: this.currentTenantUnitId,
			roleModels: this.roleModels,
			availablePeriodsByRoleType: this.availablePeriodsByRoleType,
			availableRoleTypes: this.personRoleTypes(),
			permissions: this.permissions,
			uviReceptionRights: this.uviReceptionRights(),
			isPropertyManagement: this.isPropertyManagement(),
		};
	}

	private openEditor(data: PersonEditorDialogData): void {
		const dialogRef = this.dialog.open(PersonEditorDialogComponent, {
			width: 'min(76rem, 96vw)',
			minWidth: '0',
			maxWidth: '96vw',
			maxHeight: '95vh',
			panelClass: 'person-editor-dialog-panel',
			autoFocus: false,
			restoreFocus: false,
			data,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				// Nach jedem erfolgreichen Speichern neu laden – unabhängig davon, ob ein Person-Objekt
				// zurückkam (z. B. reiner Leerstand liefert kein person). refreshRequested deckt Fehlerfälle ab.
				if (result?.saved || result?.refreshRequested) {
					this.notifyRoleChange();
				}
			});
	}

	deleteRole(role: Role): void {
		if (!role?.id || !this.canDeleteRole(role)) {
			return;
		}

		this.confirmAndDeleteRole(role);
	}

	/**
	 * Löschbar ist nur die neueste gespeicherte Rolle **ihrer eigenen Kette** — ein Löschen in der
	 * Mitte würde eine Lücke hinterlassen. Bewohner und Leerstand bilden eine Kette, alle anderen
	 * Rollentypen sind unabhängig (ein Eigentümer-Zeitraum bleibt löschbar).
	 */
	private readonly deletableRoleKeys = computed<ReadonlySet<string>>(() => {
		const newestPerChain = new Map<RoleType, Role>();
		for (const roleModel of this.roleModelsData()) {
			for (const role of roleModel.roles ?? []) {
				// Rollen ohne Id sind aufgefüllte Lücken — sie dürfen die echte letzte Rolle nicht verdecken.
				if (!role.id) {
					continue;
				}
				const chain = role.roleType === UiRoleType.VACANCY ? UiRoleType.RESIDENT : role.roleType;
				const newest = newestPerChain.get(chain);
				if (!newest || this.roleStartValue(role) > this.roleStartValue(newest)) {
					newestPerChain.set(chain, role);
				}
			}
		}
		return new Set([...newestPerChain.values()].map(roleTrackKey));
	});

	canDeleteRole(role: Role): boolean {
		return this.canDeleteProtectedRole(role) && this.deletableRoleKeys().has(roleTrackKey(role));
	}

	private canDeleteProtectedRole(role: Role): boolean {
		return !isProtectedRoleType(role?.roleType) || this.isPropertyManagement();
	}

	private roleStartValue(role: Role): number {
		return role.validFrom?.valueOf() ?? 0;
	}

	private confirmAndDeleteRole(role: Role): void {
		const dialogRef = this.dialog.open(DeleteRoleWarningDialogComponent, {
			width: 'min(58rem, 90vw)',
			maxWidth: '90vw',
			panelClass: 'delete-role-warning-dialog-panel',
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result) {
					this.executeRoleDeletion(role);
				}
			});
	}

	private executeRoleDeletion(role: Role): void {
		const realEstate = this.selectedRealEstate();
		const tenantUnit = this.selectedTenantUnit();
		const roleDelete$ = this.getRoleDeleteRequest(realEstate.area, realEstate.id, tenantUnit.id, role);
		const deleteFlow$ = roleDelete$.pipe(catchError(() => of(null)));

		if (role.roleType === UiRoleType.VACANCY) {
			const previousResidentData = this.findPreviousResidentData();
			deleteFlow$
				.pipe(
					switchMap(() => (previousResidentData ? this.restorePreviousResident(previousResidentData, realEstate, tenantUnit) : of(null))),
					takeUntilDestroyed(this.destroyRef)
				)
				.subscribe({
					next: () => this.handleDeleteSuccess(),
					error: (err) => this.handleDeleteError(err),
				});
			return;
		}

		deleteFlow$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: () => this.handleDeleteSuccess(),
			error: (err) => this.handleDeleteError(err),
		});
	}

	private findPreviousResidentData(): { role: Role; roleModel: RoleModel } | null {
		let mostRecentData: { role: Role; roleModel: RoleModel } | null = null;

		for (const roleModel of this.roleModelsData()) {
			if (roleModel.isVacancy) {
				continue;
			}

			for (const role of roleModel.roles ?? []) {
				if (role.roleType !== UiRoleType.RESIDENT || !this.roleHasEndDate(role)) {
					continue;
				}

				if (!mostRecentData || (role.validFrom && (!mostRecentData.role.validFrom || role.validFrom.isAfter(mostRecentData.role.validFrom)))) {
					mostRecentData = { role, roleModel };
				}
			}
		}

		return mostRecentData;
	}

	private restorePreviousResident(data: { role: Role; roleModel: RoleModel }, realEstate: RealEstate, tenantUnit: TenantUnitDetail): Observable<Resident> {
		const { role, roleModel } = data;
		const validFrom = role.validFrom ?? DateUtil.minDate;
		return this.residentService.updateResident(realEstate.area, realEstate.id, tenantUnit.id, role.id, {
			tenantUnitId: tenantUnit.id,
			person: roleModel.person,
			address: roleModel.person?.address,
			propertyManagementNumber: role.propertyManagementNumber,
			validFrom: this.dateUtilService.getDateWithoutTimezone(validFrom).toISOString(),
			validTo: this.dateUtilService.getDateWithoutTimezone(DateUtil.maxDate).toISOString(),
		});
	}

	private getRoleDeleteRequest(area: number, realEstateId: string, tenantUnitId: string, role: Role): Observable<unknown> {
		switch (role.roleType) {
			case UiRoleType.RESIDENT:
				return this.residentService.deleteResident(area, realEstateId, tenantUnitId, role.id);
			case UiRoleType.TENANT:
				return this.tenantService.deleteTenant(area, realEstateId, tenantUnitId, role.id);
			case UiRoleType.OWNER:
				return this.ownerService.deleteOwner(area, realEstateId, tenantUnitId, role.id);
			case UiRoleType.CONSUMPTION_INFO_RECEIVER:
				return this.consumptionInfoReceiverService.deleteConsumptionInfoReceiver(area, realEstateId, tenantUnitId, role.id);
			case UiRoleType.VACANCY:
				return this.vacancyService.deleteVacancy(area, realEstateId, tenantUnitId, role.id);
			default:
				throw new Error(`Unsupported role type for deletion: ${role.roleType}`);
		}
	}

	private handleDeleteSuccess(): void {
		this.snackBar.success(this.translocoService.translate('tenantunitDetails.roleDeleted'));
		this.notifyRoleChange();
	}

	private handleDeleteError(err: unknown): void {
		this.snackBar.apiError(err, this.translocoService.translate('tenantunitDetails.errorRoleDelete'));
	}

	/** Nur Eigentümer und Bewohner können einen Berechtigungscode bekommen. */
	canHavePermissionCode(role: Role): boolean {
		return PERMISSION_CODE_ROLE_TYPES.includes(role?.roleType);
	}

	/** null blendet das Berechtigungs-Widget (QR- und Plus-Symbol) komplett aus. */
	setPermissionDialogData(role: Role, roleModel: RoleModel): PermissionDialogParameters {
		if (!this.canHavePermissionCode(role)) {
			return null;
		}
		return {
			currentUserId: role.id,
			permissionType: role.roleType as unknown as PermissionObject.PermissionTypeEnum,
			tenantUnitId: this.selectedTenantUnit().id,
			realEstateId: this.selectedRealEstate().id,
			personEmail: roleModel?.person?.email,
		};
	}

	canOpenPermissionsDialog(role: Role): boolean {
		if (!role?.id || !this.canHavePermissionCode(role)) {
			return false;
		}
		const hasCodes = (this.permissions?.[role.id]?.length ?? 0) > 0;
		return this.isCurrentRole(role) || hasCodes;
	}

	canCreatePermission(role: Role): boolean {
		return (
			this.canHavePermissionCode(role) && this.canManagePermissionsForRole(role) && !!role?.id && this.isCurrentRole(role) && !this.roleHasEndDate(role)
		);
	}

	/** Anlegen, Bearbeiten und Löschen von Berechtigungen — an anderen Rollen als dem Bewohner nur als Hausverwaltung. */
	protected canManagePermissionsForRole(role: Role): boolean {
		return role?.roleType === UiRoleType.RESIDENT || this.isPropertyManagement();
	}

	protected permissionsDisabledReason(role: Role): string {
		return this.canManagePermissionsForRole(role) ? '' : this.translocoService.translate('permissions.onlyResidentManageable');
	}

	private processHighlighting(): void {
		if (this.personIdToHighlightNextLoad) {
			this.highlightPersonRow(this.personIdToHighlightNextLoad);
			this.personIdToHighlightNextLoad = null;
		}
	}

	private highlightPersonRow(personId: string): void {
		const targetRoleModel = this.findRoleModelByPersonId(personId);

		if (!targetRoleModel) return;

		this.startHighlightAnimation(targetRoleModel);
		this.scheduleHighlightRemoval(targetRoleModel);
	}

	private findRoleModelByPersonId(personId: string): RoleModel | undefined {
		return this.roleModelsData().find((rm) => rm.person?.id === personId && !rm.isVacancy);
	}

	private startHighlightAnimation(roleModel: RoleModel): void {
		roleModel.isHighlighting = true;
		this.refreshDataSource();
	}

	private scheduleHighlightRemoval(roleModel: RoleModel): void {
		const ANIMATION_DURATION_MS = 1000;

		setTimeout(() => {
			if (roleModel) {
				roleModel.isHighlighting = false;
				this.refreshDataSource();
			}
		}, ANIMATION_DURATION_MS);
	}

	/** Neue Array-Instanz, damit die abgeleiteten Signale die In-Place-Änderung (Highlighting) sehen. */
	private refreshDataSource(): void {
		this.roleModelsData.update((models) => [...models]);
	}

	isCurrentRole(role: Role): boolean {
		return this.roleModelService.isCurrentRole(role);
	}

	/** Heute gültige Rolle (von ≤ heute ≤ bis) — für führende/aufgeklappte Darstellung, nicht „neueste/offene". */
	isTodayRole(role: Role): boolean {
		return this.roleModelService.isTodayRole(role);
	}

	/** Status-Icon (heute/vergangen/zukünftig) für die Rollen-Chips — app-weit einheitlich. */
	roleStatusIcon(role: Role): string {
		return statusIconForStatus(DateUtil.classifyPeriod(role.validFrom, role.validTo));
	}

	// -- rollenzentrierte, gruppierte Tabelle (Liste) ---------------------------------------------

	/** Übernimmt die Spaltensortierung aus dem mat-table-Header (wirkt innerhalb der Gruppen). */
	protected onRoleSort(sort: Sort): void {
		this.roleSort.set(sort);
	}

	/** Treffer der Freitextsuche über Personenname + Zeitraum-Label eines Eintrags. */
	private entryMatchesSearch(entry: RoleSectionEntry, search: string): boolean {
		const haystack = `${this.getName(entry.roleModel)} ${this.getRolePeriodLabel(entry.role)}`.toLowerCase();
		return haystack.includes(search);
	}

	/** Sortiert die Einträge einer Gruppe: ohne aktive Spalte heute zuerst, dann Einzug absteigend. */
	private sortSectionEntries(rows: RoleSectionEntry[], sort: Sort): RoleSectionEntry[] {
		const byDefault = (a: RoleSectionEntry, b: RoleSectionEntry): number => {
			const aToday = this.isTodayRole(a.role) ? 0 : 1;
			const bToday = this.isTodayRole(b.role) ? 0 : 1;
			if (aToday !== bToday) {
				return aToday - bToday;
			}
			return (b.role.validFrom?.valueOf() ?? 0) - (a.role.validFrom?.valueOf() ?? 0);
		};
		if (!sort.active || !sort.direction) {
			return [...rows].sort(byDefault);
		}
		const dir = sort.direction === 'asc' ? 1 : -1;
		return [...rows].sort((a, b) => {
			if (sort.active === 'person') {
				return dir * this.getName(a.roleModel).localeCompare(this.getName(b.roleModel));
			}
			if (sort.active === 'period') {
				return dir * ((a.role.validFrom?.valueOf() ?? 0) - (b.role.validFrom?.valueOf() ?? 0));
			}
			return byDefault(a, b);
		});
	}

	/** Cast einer flachen Zeile auf eine Datenzeile (durch das `when`-Prädikat der Row-Defs abgesichert). */
	protected asRoleEntry(row: GroupedRow<RoleSectionEntry>): RoleSectionEntry {
		return row as RoleSectionEntry;
	}

	protected toggleRoleGroupExpanded(key: string): void {
		toggleSetKey(this.expandedRoleGroups, key);
	}

	// uVI-Zustellart → Icon + Label. Bewusst über den String-Wert statt über das Client-Enum:
	// Client-Bumps haben Enum-Formen schon mehrfach umgeworfen.
	/** Icon der uVI-Zustellart. */
	protected uviIcon(role: Role): string {
		switch (String(role?.roleSetting?.receptionType ?? '')) {
			case 'PRINT':
				return 'outgoing_mail';
			case 'PORTAL':
				return 'devices';
			default:
				return 'mail_off';
		}
	}

	/** Übersetzungsschlüssel des uVI-Zustellart-Labels. */
	protected uviLabelKey(role: Role): string {
		switch (String(role?.roleSetting?.receptionType ?? '')) {
			case 'PRINT':
				return 'uviOptions.PRINT';
			case 'PORTAL':
				return 'uviOptions.PORTAL';
			default:
				return 'uviOptions.NO_EED';
		}
	}

	/** Rollentyp einer Gruppe (aus deren `meta`) — steuert Ziel + „offene Vakanz"-Button der Add-Zeile. */
	protected addRowRoleType(group: TableGroup<RoleSectionEntry>): RoleType {
		return group.meta as RoleType;
	}

	/** Ist die Gruppe eingeklappt? */
	protected isRoleGroupCollapsed(key: string): boolean {
		return this.collapsedRoleGroups().has(key);
	}

	/** Gruppe ein-/ausklappen. */
	protected toggleRoleGroup(key: string): void {
		toggleSetKey(this.collapsedRoleGroups, key);
	}

	/**
	 * Öffnet den Anlege-Dialog für die Add-Zeile einer Sektion — immer mit dem Rollentyp DIESER Sektion
	 * in der Rollenzeile. Eigentümer und Bewohner haben dafür einen eigenen Einstiegskontext; alle
	 * übrigen (Mieter, abw. uVI-Empfänger) laufen über den allgemeinen Einstieg MIT Vorauswahl — ohne
	 * die legte der Dialog dort seine Default-Rolle „Bewohner" an.
	 */
	protected addToSection(roleType: RoleType): void {
		if (!this.canAddRoleType(roleType)) {
			return;
		}
		switch (roleType) {
			case UiRoleType.OWNER:
				this.openPersonEditorOwner();
				break;
			case UiRoleType.RESIDENT:
				this.openPersonEditorResident();
				break;
			default:
				this.openPersonEditorDialog('newPerson', [roleType]);
				break;
		}
	}

	getName(roleModel: RoleModel): string {
		return this.personDisplayUtil.getLongName(roleModel.person);
	}

	protected isWorkOrderIssued(): boolean {
		return this.realEstateSettings()?.workOrderIssued !== false;
	}

	onViewModeChange(value: unknown): void {
		this.viewMode.set(value as RoleViewMode);
	}

	getRolePeriodLabel(role: Role): string {
		return `${this.getRoleValidFromLabel(role)} – ${this.getRoleValidToLabel(role)}`;
	}

	/** True, sobald es überhaupt einen Datensatz gibt — inkl. reiner Leerstände. */
	hasAnyRole(): boolean {
		return this.roleModelsData().length > 0;
	}

	getRoleValidToLabel(role: Role): string {
		const validTo = role?.validTo;
		if (!validTo || validTo.isSame(DateUtil.maxDate, 'day')) {
			return '\u221E';
		}
		return this.dateUtilService.checkDateValid(validTo);
	}

	getRoleValidFromLabel(role: Role): string {
		const validFrom = role?.validFrom;
		if (!validFrom || validFrom.isSame(DateUtil.minDate, 'day')) {
			return '\u221E';
		}
		return this.dateUtilService.checkDateValid(validFrom);
	}

	getRoleTypeLabel(roleType: string): string {
		return this.rolesDisplay.roleTypeLabel(roleType);
	}

	private roleHasEndDate(role: Role): boolean {
		return roleHasEnd(role);
	}
}
